import { useMemo } from 'react';
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';

type InfiniteQueryFn<TParams, TData> = (
  params: TParams,
) => UseInfiniteQueryResult<InfiniteData<TData>>;

interface UseVirtualServerTablePaginationArgs<
  TBaseParams,
  TPageData,
  TItemData,
> {
  queryFn: InfiniteQueryFn<TBaseParams, TPageData>;
  baseQueryParams: TBaseParams;
  getItemsFromPage: (page: TPageData) => TItemData[];
}

export function useVirtualServerTablePagination<
  TBaseParams,
  TPageData,
  TItemData,
>(
  args: UseVirtualServerTablePaginationArgs<TBaseParams, TPageData, TItemData>,
): {
  items: TItemData[];
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  refetch: () => void;
  error: Error | null;
} {
  const { queryFn, baseQueryParams, getItemsFromPage } = args;

  // Combine all parameters for the API call
  const combinedParams = useMemo(() => {
    return {
      ...baseQueryParams,
    } as TBaseParams;
  }, [baseQueryParams]);

  const {
    data,
    isLoading,
    isFetching,
    fetchNextPage,
    isFetchingPreviousPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    error,
  } = queryFn(combinedParams);

  // Flatten all pages into a single array of items
  const items = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => getItemsFromPage(page));
  }, [data, getItemsFromPage]);

  return {
    items,
    isLoading:
      isLoading || isFetching || isFetchingNextPage || isFetchingPreviousPage,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
    error,
  };
}
